import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Mail, Copy, Inbox, Receipt, RefreshCw, Link2,
} from "lucide-react";
import { vaultSecurityApi } from "@/api/vaultSecurity";

/* Business Tools workspace — the LIVE tax receiver pipeline + inbox parsing
   engine (Component 3). Surfaces the per-tenant intake webhook URL, the inbox
   parse trigger, and the parsed `tax_receipts` ledger. */
export default function BusinessDesk({ onBack }) {
  const [webhook, setWebhook] = useState(null);
  const [webhookBusy, setWebhookBusy] = useState(false);
  const [inbox, setInbox] = useState("");
  const [inboxBusy, setInboxBusy] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [ledgerBusy, setLedgerBusy] = useState(false);

  const loadLedger = () => {
    setLedgerBusy(true);
    vaultSecurityApi.taxReceipts().then(setLedger).catch(() => {}).finally(() => setLedgerBusy(false));
  };
  useEffect(() => { loadLedger(); }, []);

  const getWebhook = async () => {
    setWebhookBusy(true);
    try { setWebhook(await vaultSecurityApi.taxWebhookUrl()); }
    catch { toast.error("Could not mint webhook"); } finally { setWebhookBusy(false); }
  };

  const connectInbox = async () => {
    if (!/^\S+@\S+\.\S+$/.test(inbox.trim())) { toast.error("Enter a valid inbox email"); return; }
    setInboxBusy(true);
    try {
      const r = await vaultSecurityApi.taxConnectInbox(inbox.trim().toLowerCase());
      toast.success(`Parsed ${r.parsed} receipts from inbox`);
      loadLedger();
    } catch { toast.error("Inbox parse failed"); } finally { setInboxBusy(false); }
  };

  return (
    <div className="absolute inset-0 z-30 flex flex-col" style={{ backgroundColor: "#A4DCFA" }} data-testid="business-desk">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3">
        <button onClick={onBack} data-testid="businessdesk-back-btn" aria-label="Back" className="w-10 h-10 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} strokeWidth={2.4} /></button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-charcoal/60">Business Tools</p>
          <h1 className="font-serif text-2xl text-charcoal flex items-center gap-1.5"><Receipt size={18} /> Tax Receiver</h1>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-6 space-y-4">
        {/* Live intake webhook */}
        <div className="border-4 border-charcoal rounded-3xl bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="business-webhook-card">
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><Link2 size={12} /> Live Intake Webhook</p>
          <p className="font-mono text-[10px] text-charcoal/60 mt-1 leading-relaxed">Point your Resend inbound route / provider at this URL. Every receipt email is parsed straight into your ledger.</p>
          <button onClick={getWebhook} disabled={webhookBusy} data-testid="business-webhook-btn" className="mt-3 w-full flex items-center justify-center gap-1.5 bg-charcoal text-gold border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">{webhookBusy ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Get Live Intake Webhook URL</button>
          {webhook?.webhook_url && (
            <div className="mt-2 border-2 border-charcoal/20 rounded-xl bg-cream p-2" data-testid="business-webhook-url">
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-[9px] text-charcoal break-all leading-snug">{webhook.webhook_url}</code>
                <button onClick={() => { navigator.clipboard?.writeText(webhook.webhook_url); toast.success("URL copied"); }} data-testid="business-webhook-copy" className="shrink-0 w-7 h-7 grid place-items-center rounded-lg bg-gold border-2 border-charcoal shadow-[1.5px_1.5px_0px_#1A1A1A]"><Copy size={12} className="text-charcoal" strokeWidth={3} /></button>
              </div>
            </div>
          )}
        </div>

        {/* Inbox parsing engine */}
        <div className="border-4 border-charcoal rounded-3xl bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="business-inbox-card">
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><Inbox size={12} /> Inbox Parsing Engine</p>
          <input value={inbox} onChange={(e) => setInbox(e.target.value)} data-testid="business-inbox-input" placeholder="billing@yourbusiness.com" className="mt-2 w-full bg-cream border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-charcoal" />
          <button onClick={connectInbox} disabled={inboxBusy} data-testid="business-inbox-btn" className="mt-2 w-full flex items-center justify-center gap-1.5 bg-gold text-black border-2 border-charcoal font-mono text-[10px] font-black uppercase tracking-widest py-2.5 rounded-full shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">{inboxBusy ? <Loader2 size={13} className="animate-spin" /> : <Inbox size={13} />} Scan & Parse Inbox</button>
        </div>

        {/* Parsed receipts ledger */}
        <div className="border-4 border-charcoal rounded-3xl bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="business-ledger-card">
          <div className="flex items-center justify-between mb-2">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><Receipt size={12} /> Parsed Receipts</p>
            <button onClick={loadLedger} data-testid="business-ledger-refresh" className="w-7 h-7 grid place-items-center rounded-lg bg-cream border-2 border-charcoal shadow-[1.5px_1.5px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">{ledgerBusy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}</button>
          </div>
          <div className="flex items-end justify-between border-b-2 border-charcoal/10 pb-2 mb-2">
            <div><p className="font-mono text-[8px] uppercase tracking-widest text-charcoal/40">Total Expenses</p><p className="font-serif text-3xl text-charcoal tabular-nums" data-testid="business-ledger-total">${(ledger?.total ?? 0).toLocaleString()}</p></div>
            <span className="font-mono text-[9px] font-black text-charcoal/60">{ledger?.count ?? 0} receipts</span>
          </div>
          <div className="space-y-2" data-testid="business-ledger-list">
            {(ledger?.receipts || []).length === 0 ? (
              <p className="font-mono text-[10px] text-charcoal/45 text-center py-6">No receipts parsed yet. Connect an inbox or use the live webhook.</p>
            ) : ledger.receipts.map((r, i) => (
              <div key={r.id || i} className="flex items-center gap-2 border-2 border-charcoal/15 rounded-xl bg-cream p-2.5" data-testid={`business-receipt-${i}`}>
                {r.live && <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-500" title="live" />}
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-[11px] font-black text-charcoal truncate">{r.vendor}</p>
                  <p className="font-mono text-[8px] text-charcoal/40">{r.date} · {r.category || "Uncategorized"}</p>
                </div>
                <span className="font-serif text-lg text-charcoal tabular-nums shrink-0">${Number(r.amount || 0).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
